// ✅ src/pages/VehicleV2/OwnVehicleAverageMileageReport.jsx
// SAME UI TEMPLATE as DayWiseLubeConsumptionReport.jsx
// ✅ Updated to use localStorage session values
// ✅ Sends backend-matching headers:
//    x-finyearid, x-finyear, x-branchid, x-branchtype, x-userid

import React, { useContext, useMemo, useState } from "react";
import axios from "axios";
import {
  FaSearch,
  FaPrint,
  FaFilter,
  FaArrowLeft,
  FaFileExport,
  FaInfoCircle,
} from "react-icons/fa";

import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";

/**
 * ✅ Backend mount:
 * app.use("/api/averageMillageReport", averageMillageReport);
 *
 * Endpoints expected:
 * GET /list
 * GET /print
 *
 * Query params:
 * fromDate=dd/MM/yyyy
 * toDate=dd/MM/yyyy
 * fuelType=Diesel|Petrol
 *
 * Headers:
 * x-finyearid
 * x-finyear
 * x-branchid
 * x-branchtype
 * x-userid
 */
const API_BASE = "http://nissi-india.com/api/averageMillageReport";

/* ---------------- helpers ---------------- */
const toStr = (v) => String(v ?? "").trim();
const pad2 = (n) => String(n).padStart(2, "0");

const isoToDDMMYYYY = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`;
};

const makeISODate = (d) =>
  `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

const fmtNum = (v, digits = 2) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return toStr(v);
  return n.toLocaleString("en-IN", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
};

export default function OwnVehicleAverageMileageReport() {
  const navigate = useNavigate();
  const { isDark, skin, setSkin } = useContext(ThemeContext);

  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  /* ---------------- localStorage session values ---------------- */
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId = localStorage.getItem("branchId") || "";
  const branchType = localStorage.getItem("branchType") || "";
  const userId = localStorage.getItem("userId") || "";

  // ✅ backend matching headers
  const apiHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branchtype": branchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branchType, userId]
  );

  const [showFilters, setShowFilters] = useState(true);
  const [loading, setLoading] = useState(false);

  // ASPX default: from = today-1 year, to = today
  const defaultFromISO = useMemo(() => {
    const d = new Date();
    d.setFullYear(d.getFullYear() - 1);
    return makeISODate(d);
  }, []);
  const defaultToISO = useMemo(() => makeISODate(new Date()), []);

  const [filters, setFilters] = useState({
    fuelType: "Diesel",
    fromISO: defaultFromISO,
    toISO: defaultToISO,
  });

  const [rows, setRows] = useState([]);

  /* ---------------- pagination ---------------- */
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const totalPages = Math.ceil(rows.length / itemsPerPage);

  const pagedRows = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return rows.slice(start, start + itemsPerPage);
  }, [rows, currentPage]);

  /* ---------------- UI classes ---------------- */
  const inputClass = `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
    isDark
      ? "bg-gray-700/30 border-gray-600 text-white"
      : "bg-white border-gray-300 text-gray-900"
  }`;

  const labelClass = `block text-sm font-medium mb-2 ${
    isDark ? "text-gray-300" : "text-gray-700"
  }`;

  /* ---------------- validation ---------------- */
  const validate = () => {
    if (!filters.fromISO) return "Please Select FromDate";
    if (!filters.toISO) return "Please Select ToDate";

    const a = new Date(filters.fromISO).getTime();
    const b = new Date(filters.toISO).getTime();

    if (Number.isNaN(a) || Number.isNaN(b)) return "Invalid Date";
    if (a > b) return "Please select FromDate less than ToDate";

    return "";
  };

  const handleSearch = async () => {
    const msg = validate();
    if (msg) {
      toast.warning(msg);
      return;
    }

    try {
      setLoading(true);
      setCurrentPage(1);

      const params = {
        fromDate: isoToDDMMYYYY(filters.fromISO),
        toDate: isoToDDMMYYYY(filters.toISO),
        fuelType: filters.fuelType,
        branchId: branchId
      };

      const { data } = await axios.get(`${API_BASE}/list`, {
        params,
        headers: apiHeaders,
      });

      if (!data?.success) {
        throw new Error(data?.message || "Search failed");
      }

      const list = Array.isArray(data.rows) ? data.rows : [];
      setRows(list);

      if (!list.length) toast.info("No Records Found");
      else toast.success(`Found ${list.length} records`);
    } catch (err) {
      setRows([]);
      toast.error(err?.response?.data?.message || err?.message || "Search failed");
    } finally {
      setLoading(false);
    }
  };

  const handleClearFilters = () => {
    setRows([]);
    setFilters({
      fuelType: "Diesel",
      fromISO: defaultFromISO,
      toISO: defaultToISO,
    });
    setCurrentPage(1);
    toast.info("Cleared filters");
  };

  const handleExport = () => {
    if (!rows.length) {
      toast.warning("No data to export");
      return;
    }

    const headers = [
      "S.No",
      "Vehicle Name",
      "Vehicle Code",
      "Vehicle No",
      "Vehicle Basis",
      "Avg Mileage/Fuel Per Hr",
    ];

    const get = (r, keys) => {
      for (const k of keys) {
        const v = r?.[k];
        if (v !== undefined && v !== null && String(v).trim() !== "") return v;
      }
      return "";
    };

    const csv = [
      headers.join(","),
      ...rows.map((r, idx) =>
        [
          idx + 1,
          get(r, ["fld_vehicle_name", "vehicle_name"]),
          get(r, ["fld_vehicle_code", "vehicle_code"]),
          get(r, ["fld_vehicle_regno", "fld_vehicle_no", "vehicle_regno"]),
          get(r, ["fld_vehicle_basistype", "vehicle_basistype"]),
          get(r, ["mileage", "avgMileage"]),
        ]
          .map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`)
          .join(",")
      ),
    ].join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = "own-vehicle-average-mileage.csv";
    a.click();

    URL.revokeObjectURL(url);
    toast.success("Exported CSV");
  };

  const handlePrint = () => {
    const msg = validate();
    if (msg) {
      toast.warning(msg);
      return;
    }

    const qs = new URLSearchParams({
      fromDate: isoToDDMMYYYY(filters.fromISO),
      toDate: isoToDDMMYYYY(filters.toISO),
      fuelType: filters.fuelType,
      finYearId,
      finYear,
      branchId,
      branchType,
      userId,
    }).toString();

    window.open(`/vehicle/OwnVehicleAverageMillageReport_Print?${qs}`, "_blank");
  };

  const tableColumns = [
    { key: "sno", label: "S.No", center: true },
    { key: "vehicleName", label: "Vehicle Name" },
    { key: "vehicleCode", label: "Vehicle Code" },
    { key: "vehicleNo", label: "Vehicle No" },
    { key: "vehicleBasis", label: "Vehicle Basis" },
    { key: "mileage", label: "Avg Mileage/Fuel Per Hr", center: true },
  ];

  const pick = (r, keys, fallback = "") => {
    for (const k of keys) {
      const v = r?.[k];
      if (v !== undefined && v !== null && String(v).trim() !== "") return v;
    }
    return fallback;
  };

  return (
    <VehicleLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={
            <span className="font-dmserif text-3xl">
              OWN VEHICLE AVERAGE MILEAGE REPORT
            </span>
          }
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-4 mb-6">
            <div className="flex items-center gap-4 flex-wrap">
              <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                Own Vehicle Average mileage Report
              </h1>

              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  isDark ? "bg-blue-900 text-blue-100" : "bg-blue-100 text-blue-800"
                }`}
              >
                {rows.length} Records
              </span>

              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  isDark ? "bg-gray-800 text-gray-200" : "bg-gray-100 text-gray-800"
                }`}
              >
                INFRA SCREEN : B-44
              </span>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <button
                onClick={() => setShowFilters((s) => !s)}
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? `border-gray-600 text-white hover:bg-gray-800${
                        showFilters ? " bg-gray-800" : ""
                      }`
                    : `border-gray-300 text-gray-700 hover:bg-gray-50${
                        showFilters ? " bg-gray-50" : ""
                      }`
                }`}
              >
                <FaFilter className="w-4 h-4" />
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>

              <button
                onClick={handleExport}
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? "border-gray-600 text-white hover:bg-gray-800"
                    : "border-gray-300 text-gray-700 hover:bg-gray-50"
                }`}
              >
                <FaFileExport className="w-4 h-4" />
                Export
              </button>

              <button
                onClick={() => navigate(-1)}
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? "border-gray-600 text-white hover:bg-gray-800"
                    : "border-gray-300 text-gray-700 hover:bg-gray-50"
                }`}
              >
                <FaArrowLeft className="w-4 h-4" />
                Back
              </button>
            </div>
          </div>

          {showFilters && (
            <div
              className={`rounded-xl p-6 mb-6 border ${
                isDark ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"
              }`}
            >
              <h3
                className={`text-lg font-semibold mb-4 flex items-center gap-2 ${
                  isDark ? "text-white" : "text-gray-900"
                }`}
              >
                <FaFilter /> FILTRATION AREA
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
                <div>
                  <label className={labelClass}>Fuel Type</label>
                  <select
                    className={inputClass}
                    value={filters.fuelType}
                    onChange={(e) =>
                      setFilters((prev) => ({ ...prev, fuelType: e.target.value }))
                    }
                  >
                    <option value="Diesel">Diesel</option>
                    <option value="Petrol">Petrol</option>
                  </select>
                </div>

                <div>
                  <label className={labelClass}>FromDate</label>
                  <input
                    type="date"
                    className={inputClass}
                    value={filters.fromISO}
                    onChange={(e) =>
                      setFilters((prev) => ({ ...prev, fromISO: e.target.value }))
                    }
                  />
                  <div className="text-xs mt-1 opacity-70">
                    {filters.fromISO ? isoToDDMMYYYY(filters.fromISO) : ""}
                  </div>
                </div>

                <div>
                  <label className={labelClass}>ToDate</label>
                  <input
                    type="date"
                    className={inputClass}
                    value={filters.toISO}
                    onChange={(e) =>
                      setFilters((prev) => ({ ...prev, toISO: e.target.value }))
                    }
                  />
                  <div className="text-xs mt-1 opacity-70">
                    {filters.toISO ? isoToDDMMYYYY(filters.toISO) : ""}
                  </div>
                </div>

                <div className="flex items-end">
                  <div className="text-sm opacity-70">
                    Default range: last 1 year (same as ASPX)
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={handleClearFilters}
                  className="text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white font-medium transition-colors duration-200"
                >
                  Clear All Filters
                </button>

                <div className="flex flex-wrap gap-4">
                  <button
                    onClick={handleSearch}
                    disabled={loading}
                    className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-8 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                      loading ? "opacity-60 cursor-not-allowed" : ""
                    }`}
                  >
                    <FaSearch />
                    {loading ? "Loading..." : "Search Records"}
                  </button>

                  <button
                    onClick={handlePrint}
                    disabled={loading}
                    className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-8 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                      loading ? "opacity-60 cursor-not-allowed" : ""
                    }`}
                  >
                    <FaPrint />
                    Print
                  </button>
                </div>
              </div>
            </div>
          )}

          <div
            className={`rounded-xl shadow-sm border overflow-hidden ${
              isDark ? "border-gray-700" : "border-gray-200"
            }`}
          >
            {rows.length === 0 ? (
              <div className="text-center py-16">
                <div
                  className={`inline-flex items-center justify-center w-20 h-20 rounded-full mb-6 ${
                    isDark ? "bg-gray-700" : "bg-gray-100"
                  }`}
                >
                  <FaInfoCircle className="w-10 h-10 text-gray-400" />
                </div>
                <h3
                  className={`text-xl font-semibold mb-2 ${
                    isDark ? "text-gray-200" : "text-gray-800"
                  }`}
                >
                  No Records
                </h3>
                <p
                  className={`mb-6 max-w-sm mx-auto ${
                    isDark ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  Select Fuel Type and Date range, then click Search.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className={`${buttonClass} text-white`}>
                    <tr>
                      {tableColumns.map((c) => (
                        <th
                          key={c.key}
                          className={`px-4 py-3 font-semibold whitespace-nowrap ${
                            c.center ? "text-center" : "text-left"
                          }`}
                        >
                          {c.label}
                        </th>
                      ))}
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {pagedRows.map((r, idx) => {
                      const sno = (currentPage - 1) * itemsPerPage + idx + 1;
                      const vehicleName = pick(r, ["fld_vehicle_name", "vehicle_name"]);
                      const vehicleCode = pick(r, ["fld_vehicle_code", "vehicle_code"]);
                      const vehicleNo = pick(r, [
                        "fld_vehicle_regno",
                        "fld_vehicle_no",
                        "vehicle_regno",
                      ]);
                      const vehicleBasis = pick(r, [
                        "fld_vehicle_basistype",
                        "vehicle_basistype",
                      ]);
                      const mileage = pick(r, ["mileage", "avgMileage"]);

                      return (
                        <tr
                          key={idx}
                          className={`transition-colors duration-200 ${
                            isDark
                              ? "bg-gray-800 hover:bg-gray-700"
                              : "bg-white hover:bg-gray-50"
                          }`}
                        >
                          <td className="px-4 py-3 text-sm text-center whitespace-nowrap">
                            {sno}
                          </td>
                          <td className="px-4 py-3 text-sm text-left whitespace-nowrap">
                            {vehicleName}
                          </td>
                          <td className="px-4 py-3 text-sm text-left whitespace-nowrap">
                            {vehicleCode}
                          </td>
                          <td className="px-4 py-3 text-sm text-left whitespace-nowrap">
                            {vehicleNo}
                          </td>
                          <td className="px-4 py-3 text-sm text-left whitespace-nowrap">
                            {vehicleBasis}
                          </td>
                          <td className="px-4 py-3 text-sm text-center whitespace-nowrap">
                            {fmtNum(mileage, 2)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {rows.length > 0 && (
            <div className="mt-6 flex flex-col sm:flex-row justify-between items-center">
              <div
                className={`text-sm mb-4 sm:mb-0 ${
                  isDark ? "text-gray-400" : "text-gray-600"
                }`}
              >
                Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
                {Math.min(currentPage * itemsPerPage, rows.length)} of {rows.length} entries
              </div>

              <nav className="flex gap-1">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className={`px-3 py-1 border rounded text-sm ${
                    currentPage === 1
                      ? isDark
                        ? "border-gray-700 text-gray-500 cursor-not-allowed"
                        : "border-gray-300 text-gray-400 cursor-not-allowed"
                      : isDark
                      ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                      : "border-gray-300 text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  Previous
                </button>

                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) pageNum = i + 1;
                  else if (currentPage <= 3) pageNum = i + 1;
                  else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
                  else pageNum = currentPage - 2 + i;

                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`px-3 py-1 border rounded text-sm ${
                        currentPage === pageNum
                          ? `${buttonClass} text-white`
                          : isDark
                          ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                          : "border-gray-300 text-gray-700 hover:bg-gray-100"
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}

                <button
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className={`px-3 py-1 border rounded text-sm ${
                    currentPage === totalPages
                      ? isDark
                        ? "border-gray-700 text-gray-500 cursor-not-allowed"
                        : "border-gray-300 text-gray-400 cursor-not-allowed"
                      : isDark
                      ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                      : "border-gray-300 text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  Next
                </button>
              </nav>
            </div>
          )}

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
}